import CTABand from "@/components/sections/CTABand";
import Reveal from "@/components/Reveal";

const TENETS = [
  {
    n: "01",
    title: "Patient experience can inform evidence strategy",
    body: "Patient voice becomes more useful when it is structured, traceable, and interpreted with methodological discipline.",
  },
  {
    n: "02",
    title: "Impact must be designed",
    body: "Engagement activity is easy to count. Evidence impact requires clear questions, interpretation, and action planning.",
  },
  {
    n: "03",
    title: "The signal must travel",
    body: "A patient-burden insight should not remain in one deck. It should inform medical, access, evidence, brand, and engagement decisions.",
  },
  {
    n: "04",
    title: "Decision-ready evidence must be defensible",
    body: "Patient experience evidence needs attention to context, bias, representativeness, triangulation, and ethical use.",
  },
];

export default function Perspective() {
  return (
    <div data-testid="page-perspective">
      {/* Hero */}
      <section className="bg-ink-0 relative overflow-hidden" data-testid="perspective-hero">
        <div className="absolute inset-0 glow-soft pointer-events-none" aria-hidden="true" />
        <div className="container-edge pt-20 lg:pt-28 pb-20 lg:pb-24 relative z-10">
          <Reveal>
            <div className="eyebrow">Perspective</div>
            <h1
              className="font-serif font-normal text-4xl md:text-5xl lg:text-[5rem] leading-[1.04] tracking-tight mt-4 text-parchment-bright max-w-5xl"
              data-testid="perspective-headline"
            >
              Patient voice is becoming a
              <span className="block italic text-parchment">strategic evidence layer for pharma.</span>
            </h1>
            <p className="mt-9 max-w-2xl text-parchment/85 text-lg leading-relaxed">
              Patient voice is no longer just a soft insight stream.
            </p>
            <p className="mt-5 max-w-2xl text-parchment/75 text-lg leading-relaxed">
              When structured with discipline, it can help connect Medical Affairs, HEOR, Market Access, Regulatory Affairs, Commercial, and Patient Engagement around a shared understanding of patient experience.
            </p>
            <p className="mt-5 max-w-2xl text-parchment/75 text-lg leading-relaxed">
              Voice2Evidence is built for this shift — translating patient voice into evidence-ready strategy that can move across functions.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Operating Tenets */}
      <section className="bg-ink-1" data-testid="tenets-section">
        <div className="container-edge py-24 lg:py-32">
          <Reveal>
            <div className="eyebrow">Operating tenets</div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.08] border border-white/[0.08]">
            {TENETS.map((t, i) => (
              <Reveal
                key={t.n}
                delay={i * 80}
                className="bg-ink-2 p-8 lg:p-10 card-dark-hover"
                data-testid={`tenet-${t.n}`}
              >
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-serif text-4xl text-rust/80">{t.n}</span>
                  <h3 className="font-serif text-2xl text-parchment-bright leading-snug">
                    {t.title}
                  </h3>
                </div>
                <p className="text-parchment/75 leading-relaxed">{t.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our View */}
      <section
        className="bg-ink-0 relative overflow-hidden border-t border-white/[0.06]"
        data-testid="perspective-our-view"
      >
        <div className="absolute inset-0 glow-rust opacity-60 pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" aria-hidden="true" />
        <div className="container-edge py-24 lg:py-32 relative z-10">
          <div className="grid grid-cols-12 gap-x-8 gap-y-10">
            <Reveal className="col-span-12 lg:col-span-4">
              <div className="eyebrow">Our view</div>
            </Reveal>
            <Reveal className="col-span-12 lg:col-span-8" delay={120}>
              <p className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-parchment-bright leading-snug max-w-3xl">
                The future is not more listening.
              </p>
              <p className="mt-4 font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-parchment-bright italic leading-snug max-w-3xl">
                The future is turning patient voice into evidence that teams can use.
              </p>
              <p className="mt-12 font-mono text-[13px] uppercase tracking-[0.22em] text-rust">
                Voice → Evidence → Strategy
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
